import { Injectable } from '@angular/core';
import { NamedLocation } from '@microsoft/microsoft-graph-types-beta';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, distinct, last, map, mergeMap, tap } from 'rxjs/operators';
import * as actions from './actions';
import { CustomNamedLocation } from './reducer';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';

@Injectable()
export class NamedLocationsEffect {
    loadNamedLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadNamedLocations),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.ajax
                    .get<
                        Array<CustomNamedLocation>
                    >(_tenant, '/api/microsoft/graph/identity/conditionalAccess/namedLocations')
                    .pipe(
                        retry(3000, 3, 'Named locations timeout'),
                        last(),
                        map((data: any) => {
                            return actions.loadNamedLocationsSuccess({ _tenant, polices: data.value });
                        }),
                        catchError((error) => of(actions.loadNamedLocationsFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    createNamedLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.createNamedLocation),
            tap(({ _tenant, location }) => console.log(location)),
            mergeMap(({ _tenant, location }) =>
                this.ajax
                    .post<NamedLocation>(
                        _tenant,
                        '/api/microsoft/graph/identity/conditionalAccess/namedLocations',
                        location,
                    )
                    .pipe(
                        map((created) => actions.createNamedLocationSuccess({ _tenant, location: created })),
                        catchError((error) => of(actions.createNamedLocationFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    updateNamedLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateNamedLocation),
            mergeMap(({ _tenant, location }) =>
                this.ajax
                    .patch<NamedLocation>(
                        _tenant,
                        `/api/microsoft/graph/identity/conditionalAccess/namedLocations/${location.id}`,
                        location,
                    )
                    .pipe(
                        // return empty string
                        map((_) => actions.updateNamedLocationSuccess({ _tenant, location })),
                        catchError((error) => of(actions.updateNamedLocationFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    deleteNamedLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.deleteNamedLocation),
            mergeMap(({ _tenant, id }) =>
                this.ajax
                    .delete<any>(_tenant, `/api/microsoft/graph/identity/conditionalAccess/namedLocations/${id}`)
                    .pipe(
                        map(() => actions.deleteNamedLocationSuccess({ _tenant, id })),
                        catchError((error) => of(actions.deleteNamedLocationFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
    ) {}
}
